import { defineMessages } from 'react-intl-next';

/**
 * Title-list keywords. English-seeded from the project poster (meeting-language
 * + agenda/notes-language sets); defined via the standard i18n package so they
 * can localize per locale over time. There is no locale segmentation of the
 * event (decided, STARB-3768) — the English seed undercounts non-English users,
 * a documented caveat.
 *
 * Each entry is a single keyword/phrase; the whole set is materialized into a
 * string list at match time (see `getTitleKeywords`). All matched whole-word,
 * case-insensitive.
 */
export const titleKeywordMessages: {
	meeting: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	meetings: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	sync: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	standup: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	dailyStandup: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	huddle: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	checkIn: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	oneOnOne: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	kickoff: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	planning: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	sprintPlanning: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	retro: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	retrospective: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	review: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	demo: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	showcase: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	workshop: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	workingSession: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	brainstorm: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	officeHours: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	townHall: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	allHands: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	leadershipSync: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	staffMeeting: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	teamMeeting: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	customerCall: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	discoveryCall: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	designReview: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	architectureReview: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	backlogRefinement: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	agenda: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	meetingAgenda: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	meetingNotes: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	discussionTopics: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	discussion: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	actionItems: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	decisions: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	decisionLog: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	nextSteps: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	attendees: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	participants: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	outcomes: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	parkingLot: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	meeting: {
		id: 'meeting-signal-instrumentation.title-keyword.meeting',
		defaultMessage: 'meeting',
		description: 'Title keyword indicating a page is meeting-related.',
	},
	meetings: {
		id: 'meeting-signal-instrumentation.title-keyword.meetings',
		defaultMessage: 'meetings',
		description: 'Plural title keyword indicating a page is meeting-related.',
	},
	sync: {
		id: 'meeting-signal-instrumentation.title-keyword.sync',
		defaultMessage: 'sync',
		description: 'Title keyword for a sync meeting.',
	},
	standup: {
		id: 'meeting-signal-instrumentation.title-keyword.standup',
		defaultMessage: 'standup',
		description: 'Title keyword for a standup meeting.',
	},
	dailyStandup: {
		id: 'meeting-signal-instrumentation.title-keyword.daily-standup',
		defaultMessage: 'daily standup',
		description: 'Title phrase for a daily standup meeting.',
	},
	huddle: {
		id: 'meeting-signal-instrumentation.title-keyword.huddle',
		defaultMessage: 'huddle',
		description: 'Title keyword for a huddle meeting.',
	},
	checkIn: {
		id: 'meeting-signal-instrumentation.title-keyword.check-in',
		defaultMessage: 'check-in',
		description: 'Title keyword for a check-in meeting.',
	},
	oneOnOne: {
		id: 'meeting-signal-instrumentation.title-keyword.one-on-one',
		defaultMessage: '1:1',
		description: 'Title keyword for a one-on-one meeting.',
	},
	kickoff: {
		id: 'meeting-signal-instrumentation.title-keyword.kickoff',
		defaultMessage: 'kickoff',
		description: 'Title keyword for a kickoff meeting.',
	},
	planning: {
		id: 'meeting-signal-instrumentation.title-keyword.planning',
		defaultMessage: 'planning',
		description: 'Title keyword for a planning meeting.',
	},
	sprintPlanning: {
		id: 'meeting-signal-instrumentation.title-keyword.sprint-planning',
		defaultMessage: 'sprint planning',
		description: 'Title phrase for a sprint planning meeting.',
	},
	retro: {
		id: 'meeting-signal-instrumentation.title-keyword.retro',
		defaultMessage: 'retro',
		description: 'Title keyword (shorthand) for a retrospective meeting.',
	},
	retrospective: {
		id: 'meeting-signal-instrumentation.title-keyword.retrospective',
		defaultMessage: 'retrospective',
		description: 'Title keyword for a retrospective meeting.',
	},
	review: {
		id: 'meeting-signal-instrumentation.title-keyword.review',
		defaultMessage: 'review',
		description: 'Title keyword for a review meeting.',
	},
	demo: {
		id: 'meeting-signal-instrumentation.title-keyword.demo',
		defaultMessage: 'demo',
		description: 'Title keyword for a demo meeting.',
	},
	showcase: {
		id: 'meeting-signal-instrumentation.title-keyword.showcase',
		defaultMessage: 'showcase',
		description: 'Title keyword for a showcase meeting.',
	},
	workshop: {
		id: 'meeting-signal-instrumentation.title-keyword.workshop',
		defaultMessage: 'workshop',
		description: 'Title keyword for a workshop.',
	},
	workingSession: {
		id: 'meeting-signal-instrumentation.title-keyword.working-session',
		defaultMessage: 'working session',
		description: 'Title phrase for a working session.',
	},
	brainstorm: {
		id: 'meeting-signal-instrumentation.title-keyword.brainstorm',
		defaultMessage: 'brainstorm',
		description: 'Title keyword for a brainstorm meeting.',
	},
	officeHours: {
		id: 'meeting-signal-instrumentation.title-keyword.office-hours',
		defaultMessage: 'office hours',
		description: 'Title phrase for office hours.',
	},
	townHall: {
		id: 'meeting-signal-instrumentation.title-keyword.town-hall',
		defaultMessage: 'town hall',
		description: 'Title phrase for a town hall meeting.',
	},
	allHands: {
		id: 'meeting-signal-instrumentation.title-keyword.all-hands',
		defaultMessage: 'all hands',
		description: 'Title phrase for an all-hands meeting.',
	},
	leadershipSync: {
		id: 'meeting-signal-instrumentation.title-keyword.leadership-sync',
		defaultMessage: 'leadership sync',
		description: 'Title phrase for a leadership sync meeting.',
	},
	staffMeeting: {
		id: 'meeting-signal-instrumentation.title-keyword.staff-meeting',
		defaultMessage: 'staff meeting',
		description: 'Title phrase for a staff meeting.',
	},
	teamMeeting: {
		id: 'meeting-signal-instrumentation.title-keyword.team-meeting',
		defaultMessage: 'team meeting',
		description: 'Title phrase for a team meeting.',
	},
	customerCall: {
		id: 'meeting-signal-instrumentation.title-keyword.customer-call',
		defaultMessage: 'customer call',
		description: 'Title phrase for a customer call.',
	},
	discoveryCall: {
		id: 'meeting-signal-instrumentation.title-keyword.discovery-call',
		defaultMessage: 'discovery call',
		description: 'Title phrase for a discovery call.',
	},
	designReview: {
		id: 'meeting-signal-instrumentation.title-keyword.design-review',
		defaultMessage: 'design review',
		description: 'Title phrase for a design review meeting.',
	},
	architectureReview: {
		id: 'meeting-signal-instrumentation.title-keyword.architecture-review',
		defaultMessage: 'architecture review',
		description: 'Title phrase for an architecture review meeting.',
	},
	backlogRefinement: {
		id: 'meeting-signal-instrumentation.title-keyword.backlog-refinement',
		defaultMessage: 'backlog refinement',
		description: 'Title phrase for a backlog refinement meeting.',
	},
	agenda: {
		id: 'meeting-signal-instrumentation.title-keyword.agenda',
		defaultMessage: 'agenda',
		description: 'Title keyword indicating a meeting agenda.',
	},
	meetingAgenda: {
		id: 'meeting-signal-instrumentation.title-keyword.meeting-agenda',
		defaultMessage: 'meeting agenda',
		description: 'Title phrase indicating a meeting agenda.',
	},
	meetingNotes: {
		id: 'meeting-signal-instrumentation.title-keyword.meeting-notes',
		defaultMessage: 'meeting notes',
		description: 'Title phrase indicating meeting notes.',
	},
	discussionTopics: {
		id: 'meeting-signal-instrumentation.title-keyword.discussion-topics',
		defaultMessage: 'discussion topics',
		description: 'Title phrase indicating meeting discussion topics.',
	},
	discussion: {
		id: 'meeting-signal-instrumentation.title-keyword.discussion',
		defaultMessage: 'discussion',
		description: 'Title keyword indicating a meeting discussion.',
	},
	actionItems: {
		id: 'meeting-signal-instrumentation.title-keyword.action-items',
		defaultMessage: 'action items',
		description: 'Title phrase indicating meeting action items.',
	},
	decisions: {
		id: 'meeting-signal-instrumentation.title-keyword.decisions',
		defaultMessage: 'decisions',
		description: 'Title keyword indicating meeting decisions.',
	},
	decisionLog: {
		id: 'meeting-signal-instrumentation.title-keyword.decision-log',
		defaultMessage: 'decision log',
		description: 'Title phrase indicating a decision log.',
	},
	nextSteps: {
		id: 'meeting-signal-instrumentation.title-keyword.next-steps',
		defaultMessage: 'next steps',
		description: 'Title phrase indicating meeting next steps.',
	},
	attendees: {
		id: 'meeting-signal-instrumentation.title-keyword.attendees',
		defaultMessage: 'attendees',
		description: 'Title keyword indicating meeting attendees.',
	},
	participants: {
		id: 'meeting-signal-instrumentation.title-keyword.participants',
		defaultMessage: 'participants',
		description: 'Title keyword indicating meeting participants.',
	},
	outcomes: {
		id: 'meeting-signal-instrumentation.title-keyword.outcomes',
		defaultMessage: 'outcomes',
		description: 'Title keyword indicating meeting outcomes.',
	},
	parkingLot: {
		id: 'meeting-signal-instrumentation.title-keyword.parking-lot',
		defaultMessage: 'parking lot',
		description: 'Title phrase indicating a meeting parking lot.',
	},
});

/**
 * Content-list keywords. English-seeded from the poster (scheduling-language +
 * meeting-outcomes sets); a separate list from the title list (matching
 * decision, STARB-3768). All matched whole-word, case-insensitive.
 */
export const contentKeywordMessages: {
	letsSync: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	letsDiscuss: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	letsReview: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	scheduleAMeeting: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	scheduleACall: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	bookTime: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	findTime: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	calendarInvite: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	invite: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	joinTheMeeting: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	joinTheCall: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	zoom: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	googleMeet: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	microsoftTeams: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	webex: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	recap: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	meetingSummary: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	summary: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	asDiscussed: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	followUp: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	followUpHyphenated: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	nextSteps: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	decisionsMade: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	actionItems: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	takeaways: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	owners: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	discussionNotes: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	letsSync: {
		id: 'meeting-signal-instrumentation.content-keyword.lets-sync',
		defaultMessage: "let's sync",
		description: 'Content phrase inviting a sync meeting.',
	},
	letsDiscuss: {
		id: 'meeting-signal-instrumentation.content-keyword.lets-discuss',
		defaultMessage: "let's discuss",
		description: 'Content phrase inviting a discussion.',
	},
	letsReview: {
		id: 'meeting-signal-instrumentation.content-keyword.lets-review',
		defaultMessage: "let's review",
		description: 'Content phrase inviting a review.',
	},
	scheduleAMeeting: {
		id: 'meeting-signal-instrumentation.content-keyword.schedule-a-meeting',
		defaultMessage: 'schedule a meeting',
		description: 'Content phrase proposing a meeting.',
	},
	scheduleACall: {
		id: 'meeting-signal-instrumentation.content-keyword.schedule-a-call',
		defaultMessage: 'schedule a call',
		description: 'Content phrase proposing a call.',
	},
	bookTime: {
		id: 'meeting-signal-instrumentation.content-keyword.book-time',
		defaultMessage: 'book time',
		description: 'Content phrase proposing to book time.',
	},
	findTime: {
		id: 'meeting-signal-instrumentation.content-keyword.find-time',
		defaultMessage: 'find time',
		description: 'Content phrase proposing to find time to meet.',
	},
	calendarInvite: {
		id: 'meeting-signal-instrumentation.content-keyword.calendar-invite',
		defaultMessage: 'calendar invite',
		description: 'Content phrase referencing a calendar invite.',
	},
	invite: {
		id: 'meeting-signal-instrumentation.content-keyword.invite',
		defaultMessage: 'invite',
		description: 'Content keyword referencing a meeting invite.',
	},
	joinTheMeeting: {
		id: 'meeting-signal-instrumentation.content-keyword.join-the-meeting',
		defaultMessage: 'join the meeting',
		description: 'Content phrase referencing joining a meeting.',
	},
	joinTheCall: {
		id: 'meeting-signal-instrumentation.content-keyword.join-the-call',
		defaultMessage: 'join the call',
		description: 'Content phrase referencing joining a call.',
	},
	zoom: {
		id: 'meeting-signal-instrumentation.content-keyword.zoom',
		defaultMessage: 'Zoom',
		description: 'Content keyword naming the Zoom meeting tool.',
	},
	googleMeet: {
		id: 'meeting-signal-instrumentation.content-keyword.google-meet',
		defaultMessage: 'Google Meet',
		description: 'Content phrase naming the Google Meet meeting tool.',
	},
	microsoftTeams: {
		id: 'meeting-signal-instrumentation.content-keyword.microsoft-teams',
		defaultMessage: 'Microsoft Teams',
		description: 'Content phrase naming the Microsoft Teams meeting tool.',
	},
	webex: {
		id: 'meeting-signal-instrumentation.content-keyword.webex',
		defaultMessage: 'Webex',
		description: 'Content keyword naming the Webex meeting tool.',
	},
	recap: {
		id: 'meeting-signal-instrumentation.content-keyword.recap',
		defaultMessage: 'recap',
		description: 'Content keyword for a meeting recap.',
	},
	meetingSummary: {
		id: 'meeting-signal-instrumentation.content-keyword.meeting-summary',
		defaultMessage: 'meeting summary',
		description: 'Content phrase for a meeting summary.',
	},
	summary: {
		id: 'meeting-signal-instrumentation.content-keyword.summary',
		defaultMessage: 'summary',
		description: 'Content keyword for a meeting summary.',
	},
	asDiscussed: {
		id: 'meeting-signal-instrumentation.content-keyword.as-discussed',
		defaultMessage: 'as discussed',
		description: 'Content phrase referencing a prior discussion.',
	},
	followUp: {
		id: 'meeting-signal-instrumentation.content-keyword.follow-up',
		defaultMessage: 'follow up',
		description: 'Content phrase referencing a meeting follow up.',
	},
	followUpHyphenated: {
		id: 'meeting-signal-instrumentation.content-keyword.follow-up-hyphenated',
		defaultMessage: 'follow-up',
		description: 'Hyphenated content phrase referencing a meeting follow-up.',
	},
	nextSteps: {
		id: 'meeting-signal-instrumentation.content-keyword.next-steps',
		defaultMessage: 'next steps',
		description: 'Content phrase referencing meeting next steps.',
	},
	decisionsMade: {
		id: 'meeting-signal-instrumentation.content-keyword.decisions-made',
		defaultMessage: 'decisions made',
		description: 'Content phrase referencing decisions made in a meeting.',
	},
	actionItems: {
		id: 'meeting-signal-instrumentation.content-keyword.action-items',
		defaultMessage: 'action items',
		description: 'Content phrase referencing meeting action items.',
	},
	takeaways: {
		id: 'meeting-signal-instrumentation.content-keyword.takeaways',
		defaultMessage: 'takeaways',
		description: 'Content keyword referencing meeting takeaways.',
	},
	owners: {
		id: 'meeting-signal-instrumentation.content-keyword.owners',
		defaultMessage: 'owners',
		description: 'Content keyword referencing action-item owners.',
	},
	discussionNotes: {
		id: 'meeting-signal-instrumentation.content-keyword.discussion-notes',
		defaultMessage: 'discussion notes',
		description: 'Content phrase referencing meeting discussion notes.',
	},
});
